// Refine light: the open questions of a meeting ticket and the answers given
// on the meeting page or the magic link.
//
// `meeting_draft` writes one specific question per field the meeting left
// open; the server checks each one (`apps/server/src/meetings/questions.ts`),
// and a field whose question is missing or fails a check is asked its fixed
// question naming the issue (`fallbackQuestionOf`, flagged `fixed`): an open
// field always keeps its ask. A fixed question is withdrawn once the issue
// passes DoR or its body fills the field, never once it has an answer and
// never a model's own (`withoutSettledQuestions`, owner 2026-10-30). The
// questions are stored on `meeting_ticket` (`questions_*`,
// kept past filing), the answers in `meeting_answer`, keyed by ticket and
// field. An answer counts in refine directly (owner decision 2026-10-13):
// the field is green from it, with the provenance on the card
// (`meetingAnswer`), and refine's Accept is the one writer of the issue body.
//
// Pure data and functions: the web imports this. No regex lookbehind.

import { z } from "zod";

/**
 * The card fields a meeting question may ask, in the order they are asked
 * (`role`, then `means`, then `ends`, then `acceptance_criteria`). These are
 * the readiness card's own field names (`@loom/shared/readiness`), so an
 * answer lands on exactly that field in refine. Nothing else is asked here:
 * priority, milestone, definition of done, story points and sizing stay in
 * refine. Also the `meeting_answer.field` CHECK.
 */
export const MEETING_QUESTION_FIELDS = ["role", "means", "ends", "acceptance_criteria"] as const;
export type MeetingQuestionField = (typeof MEETING_QUESTION_FIELDS)[number];

/** An epic is asked about its role and goal only (its criteria wait for the split). */
export const EPIC_QUESTION_FIELDS: readonly MeetingQuestionField[] = ["role", "means"];

/**
 * How the meeting left a field: `needs_input`, discussed but not settled
 * (asked first, and may carry Loom's draft); `not_discussed`, nobody said
 * anything about it (never a draft: a customer must not click to accept
 * wording Loom made up).
 */
export const MEETING_QUESTION_STATUSES = ["needs_input", "not_discussed"] as const;
export type MeetingQuestionStatus = (typeof MEETING_QUESTION_STATUSES)[number];

/** The existing answer controls only: `text`, and `list_builder` for criteria. */
export const MEETING_QUESTION_WIDGETS = ["text", "list_builder"] as const;
export type MeetingQuestionWidget = (typeof MEETING_QUESTION_WIDGETS)[number];

/** At most this many questions per ticket, and per epic: every field the
 *  class is asked about. */
export const MEETING_QUESTIONS_PER_TICKET = 4;
export const MEETING_QUESTIONS_PER_EPIC = 2;

/** A question longer than this gives way to its field's fixed one (a question is one sentence). */
export const MEETING_QUESTION_MAX_CHARS = 200;
/** Loom's draft answer longer than this is dropped. */
export const MEETING_QUESTION_DRAFT_MAX_CHARS = 400;
/** An answer longer than this is refused (400). */
export const MEETING_ANSWER_MAX_CHARS = 2_000;

/**
 * The state of an answer row (`meeting_answer.state`):
 * - `answered`: the owner or a link holder answered; the text counts in refine;
 * - `skipped`: "Weiss ich nicht": the question goes to refine as it is;
 * - `closed`: the owner (or a link holder) filled the field through
 *   "Bearbeiten"; the issue body carries it now, no text is kept.
 */
export const MEETING_ANSWER_STATES = ["answered", "skipped", "closed"] as const;
export type MeetingAnswerState = (typeof MEETING_ANSWER_STATES)[number];

/** Who answered: the meeting's owner, or someone holding its magic link. */
export const MEETING_ANSWER_VIA = ["owner", "link"] as const;
export type MeetingAnswerVia = (typeof MEETING_ANSWER_VIA)[number];

/** The issue's title in a fixed question (`{T}`), per field: an epic's means is its goal. */
const FALLBACK_QUESTIONS: Readonly<
  Record<"de" | "en", Readonly<Record<MeetingQuestionField | "epic_means", string>>>
> = {
  de: {
    role: "Wer braucht «{T}»?",
    means: "Was genau soll sich mit «{T}» ändern?",
    epic_means: "Welches Ziel hat «{T}»?",
    ends: "Was ist nach «{T}» besser als heute?",
    acceptance_criteria: "Woran erkennt ihr, dass «{T}» fertig ist?",
  },
  en: {
    role: "Who needs “{T}”?",
    means: "What exactly should change with “{T}”?",
    epic_means: "What is the goal of “{T}”?",
    ends: "What is better than today once “{T}” is done?",
    acceptance_criteria: "How will you know that “{T}” is done?",
  },
};

const templateOf = (field: MeetingQuestionField, cls: "ticket" | "epic", lang: "de" | "en"): string =>
  FALLBACK_QUESTIONS[lang][cls === "epic" && field === "means" ? "epic_means" : field];

/**
 * A field's fixed question, naming the issue by its title: what an open
 * field asks when the model wrote no question for it or its question failed
 * a check. The title is clipped (ending in "…") so the whole stays within
 * `MEETING_QUESTION_MAX_CHARS`.
 */
export const fallbackQuestionOf = (
  field: MeetingQuestionField,
  cls: "ticket" | "epic",
  lang: "de" | "en",
  title: string,
): string => {
  const template = templateOf(field, cls, lang);
  const room = MEETING_QUESTION_MAX_CHARS - (template.length - "{T}".length);
  const name = title.replace(/\s+/gu, " ").trim();
  let clipped = name;
  if (name.length > room) {
    clipped = "";
    // By code point, so a clip never splits a character.
    for (const ch of name) {
      if (clipped.length + ch.length > room - 1) break;
      clipped += ch;
    }
    clipped = `${clipped.trimEnd()}…`;
  }
  // A function, so a "$&" in the title stays as it is.
  return template.replace("{T}", () => clipped);
};

/**
 * Whether `text` is the field's fixed question (`fallbackQuestionOf`) for
 * this class and language, whatever title it names: the template's words
 * before and after the title, with a title between them. A question stored
 * before the `fixed` flag is recognised this way, and a row filed long ago
 * has no title to compare with.
 */
export const isFixedQuestion = (
  text: string,
  field: MeetingQuestionField,
  cls: "ticket" | "epic",
  lang: "de" | "en",
): boolean => {
  const template = templateOf(field, cls, lang);
  const at = template.indexOf("{T}");
  const before = template.slice(0, at);
  const after = template.slice(at + "{T}".length);
  const t = text.replace(/\s+/gu, " ").trim();
  return t.length > before.length + after.length && t.startsWith(before) && t.endsWith(after);
};

/** The control a field's question uses. */
export const widgetOfQuestionField = (field: MeetingQuestionField): MeetingQuestionWidget =>
  field === "acceptance_criteria" ? "list_builder" : "text";

/** The meeting moment a question is about: one of the ticket's verified quotes. */
export const MeetingQuestionAnchorSchema = z.object({
  text: z.string().min(1),
  approxMs: z.number().int().nonnegative(),
});
export type MeetingQuestionAnchor = z.infer<typeof MeetingQuestionAnchorSchema>;

/** One checked question, as `meeting_ticket.questions` stores it. */
export const MeetingQuestionSchema = z.object({
  field: z.enum(MEETING_QUESTION_FIELDS),
  status: z.enum(MEETING_QUESTION_STATUSES),
  question: z.string().min(1).max(MEETING_QUESTION_MAX_CHARS),
  widget: z.enum(MEETING_QUESTION_WIDGETS),
  /** Loom's draft answer, only for `needs_input`; one criterion per line for a list. */
  draft: z.string().min(1).max(MEETING_QUESTION_DRAFT_MAX_CHARS).nullable(),
  anchor: MeetingQuestionAnchorSchema.nullable(),
  /** The field's fixed question (`fallbackQuestionOf`), not the model's
   *  own; absent on a model's question and on a row written before. */
  fixed: z.boolean().optional(),
});
export type MeetingQuestion = z.infer<typeof MeetingQuestionSchema>;

/**
 * One line of the item's own that refine reads beside the stored quotes:
 * its text (sealed like the row), the line's `seq` and the kind of
 * information it gives (`role`, named as a stored quote's: `change`,
 * `criterion`, `context`, …).
 */
export const MeetingRefineQuoteSchema = z.object({
  text: z.string().min(1),
  seq: z.number().int().nonnegative(),
  role: z.string(),
});
export type MeetingRefineQuote = z.infer<typeof MeetingRefineQuoteSchema>;

/**
 * What `meeting_ticket.questions` holds: the checked questions, and the role
 * the meeting itself named (Loom's wording, grounded in its quotes). The body
 * has no role section, so the role lives here until refine's Accept writes
 * it: refine offers it as Loom's draft (owner decision 2026-10-13, "Loom
 * drafts it, refine writes it"). `refineQuotes`: every own line of the item
 * that is not a stored quote, for refine only (the issue body keeps its few
 * quotes); absent on a row written before it.
 */
export const MeetingTicketQuestionsSchema = z.object({
  questions: z.array(MeetingQuestionSchema),
  roleDraft: z.string().min(1).max(MEETING_QUESTION_DRAFT_MAX_CHARS).nullable(),
  refineQuotes: z.array(MeetingRefineQuoteSchema).optional(),
});
export type MeetingTicketQuestions = z.infer<typeof MeetingTicketQuestionsSchema>;

const STATUS_RANK: Readonly<Record<MeetingQuestionStatus, number>> = {
  needs_input: 0,
  not_discussed: 1,
};

/**
 * The questions a ticket shows, in order and capped: one per field (the
 * first wins), only the class's fields (an epic: role and goal), discussed
 * but not settled before not discussed, and within each group role, means,
 * ends, criteria. A `not_discussed` question never carries a draft.
 */
export const orderMeetingQuestions = (
  questions: readonly MeetingQuestion[],
  cls: "ticket" | "epic",
): MeetingQuestion[] => {
  const allowed: readonly MeetingQuestionField[] =
    cls === "epic" ? EPIC_QUESTION_FIELDS : MEETING_QUESTION_FIELDS;
  const seen = new Set<MeetingQuestionField>();
  const kept: MeetingQuestion[] = [];
  for (const q of questions) {
    if (!allowed.includes(q.field) || seen.has(q.field)) continue;
    seen.add(q.field);
    kept.push(q.status === "not_discussed" && q.draft !== null ? { ...q, draft: null } : q);
  }
  const cap = cls === "epic" ? MEETING_QUESTIONS_PER_EPIC : MEETING_QUESTIONS_PER_TICKET;
  return kept
    .sort(
      (a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
        MEETING_QUESTION_FIELDS.indexOf(a.field) - MEETING_QUESTION_FIELDS.indexOf(b.field),
    )
    .slice(0, cap);
};

/** The editor's view of an issue body (`editorViewOf`, `@loom/shared/issue-body`). */
export interface EditedFields {
  readonly description: string;
  readonly outcome: string;
  readonly acceptanceCriteria: readonly string[];
}

const criteriaOf = (rows: readonly string[]): string[] =>
  rows.map((r) => r.trim()).filter((r) => r !== "");

/**
 * The card fields an issue body already fills: its description is `means`,
 * its outcome `ends`, its criteria `acceptance_criteria` (a ticket's only:
 * an epic's criteria wait for the split). The role has no body section.
 */
export const filledFieldsOf = (view: EditedFields, cls: "ticket" | "epic"): ReadonlySet<MeetingQuestionField> => {
  const out = new Set<MeetingQuestionField>();
  if (view.description.trim() !== "") out.add("means");
  if (cls === "ticket" && view.outcome.trim() !== "") out.add("ends");
  if (cls === "ticket" && criteriaOf(view.acceptanceCriteria).length > 0) out.add("acceptance_criteria");
  return out;
};

/** Why a fixed question was withdrawn (`withoutSettledQuestions`), for the content-free counts. */
export const QUESTION_WITHDRAW_REASONS = ["dor_pass", "filled"] as const;
export type QuestionWithdrawReason = (typeof QUESTION_WITHDRAW_REASONS)[number];

/**
 * The questions an issue still asks once something settled their field
 * (owner 2026-10-30, "no template question once DoR passes"): a FIXED
 * question (`fixed`, or one stored before the flag that reads as its
 * field's template, `isFixedQuestion`) is withdrawn when the issue passed
 * DoR (`dor_pass`) or its body already fills the field (`filled`), but
 * never once its field has an answer (`answered`: any `meeting_answer`
 * row, "Weiss ich nicht" too). A model's own question is never withdrawn:
 * it asks something the issue does not say. The rest keep their order.
 */
export const withoutSettledQuestions = (
  questions: readonly MeetingQuestion[],
  ctx: {
    readonly cls: "ticket" | "epic";
    readonly lang: "de" | "en";
    readonly dorPass: boolean;
    readonly filled: ReadonlySet<MeetingQuestionField>;
    readonly answered: ReadonlySet<MeetingQuestionField>;
  },
): { readonly kept: MeetingQuestion[]; readonly withdrawn: Readonly<Record<QuestionWithdrawReason, number>> } => {
  const withdrawn: Record<QuestionWithdrawReason, number> = { dor_pass: 0, filled: 0 };
  const kept = questions.filter((q) => {
    const fixed = q.fixed === true || isFixedQuestion(q.question, q.field, ctx.cls, ctx.lang);
    if (!fixed || ctx.answered.has(q.field)) return true;
    const reason: QuestionWithdrawReason | null = ctx.dorPass ? "dor_pass" : ctx.filled.has(q.field) ? "filled" : null;
    if (reason === null) return true;
    withdrawn[reason] += 1;
    return false;
  });
  return { kept, withdrawn };
};

/**
 * Which card fields a "Bearbeiten" filled, from the issue body before and
 * after the edit: a field counts only when the edit CHANGED it and left it
 * non-empty. The description is `means`, the outcome `ends`, the criteria
 * `acceptance_criteria`; the role has no form field. The form sends every
 * field, so the form alone cannot tell: an edit that only fixed the title
 * fills nothing, and a field someone filled in the issue before stays as it
 * was (its answer with it).
 */
export const questionFieldsFilledByEdit = (
  before: EditedFields,
  after: EditedFields,
): MeetingQuestionField[] => {
  const out: MeetingQuestionField[] = [];
  const text = (s: string): string => s.trim();
  if (text(after.description) !== "" && text(after.description) !== text(before.description)) {
    out.push("means");
  }
  if (text(after.outcome) !== "" && text(after.outcome) !== text(before.outcome)) {
    out.push("ends");
  }
  const was = criteriaOf(before.acceptanceCriteria);
  const now = criteriaOf(after.acceptanceCriteria);
  if (now.length > 0 && (now.length !== was.length || now.some((c, i) => c !== was[i]))) {
    out.push("acceptance_criteria");
  }
  return out;
};
